import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Row, Col, Form, Spinner, Alert } from "react-bootstrap";
import api from "../../../services/api";
import { ResponsiveBar } from "@nivo/bar";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { getCurrentUser } from "../../../common/utils";
import { programaProp } from "../../../common/propTypes";

const formatMonth = (dateString) => {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("pt-BR", { month: "short" });
};

const MonthlyProceduresDashboard = ({ programaId }) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState({
    value: currentYear,
    label: `${currentYear}`,
  });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedAssessor, setSelectedAssessor] = useState(null);

  const [stateOptions, setStateOptions] = useState([]);
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [assessorOptions, setAssessorOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let i = 0; i < 5; i++) {
      years.push({ value: currentYear - i, label: `${currentYear - i}` });
    }
    return years;
  }, [currentYear]);

  const fetchData = useCallback(async () => {
    if (!programaId) {
      setRawData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams();
    if (programaId) params.append("programa_id", programaId);
    if (selectedYear) params.append("ano", selectedYear.value);
    if (selectedPatient) params.append("paciente_id", selectedPatient.value);
    if (selectedState) params.append("estado", selectedState.value);
    if (selectedProcedure)
      params.append("procedimento_id", selectedProcedure.value);
    if (selectedDoctor) params.append("medico_id", selectedDoctor.value);
    if (selectedAssessor) params.append("assessor_id", selectedAssessor.value);

    try {
      const response = await api.get(
        `/dashboards/procedimentos_realizados/?${params.toString()}`,
        {
          headers: {
            'X-Programa-ID': programaId
          }
        }
      );
      setRawData(response.data);
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
      setError("Não foi possível carregar os dados.");
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [
    programaId,
    selectedYear,
    selectedPatient,
    selectedState,
    selectedProcedure,
    selectedDoctor,
    selectedAssessor,
  ]);

  const fetchFilterOptions = useCallback(async () => {
    if (!programaId) {
      setStateOptions([]);
      setProcedureOptions([]);
      setDoctorOptions([]);
      setAssessorOptions([]);
      return;
    }
    setLoadingOptions(true);
    const queryLimit = 1000;
    try {
      const [statesRes, proceduresRes, doctorsRes, assessorsRes] =
        await Promise.all([
          api.get(`/filters/estados/`),
          api.get(
            `/programas/servicos_procedimentos/?programa=${programaId}&status=true&limit=${queryLimit}`
          ),
          api.get(
            `/pessoas/prestadores/?categoria=medicos&programa_id=${programaId}&is_active=1&limit=${queryLimit}`
          ),
          api.get(
            `/pessoas/prestadores/?categoria=multiprofissionais&programa_id=${programaId}&is_active=1&limit=${queryLimit}`
          ),
        ]);

      setStateOptions(statesRes.data || []);
      setProcedureOptions(
        (proceduresRes.data.results || []).map((item) => ({
          value: item.servico_procedimento_id,
          label:
            item.dados_servico?.nome || `ID ${item.servico_procedimento_id}`,
        }))
      );
      setDoctorOptions(
        (doctorsRes.data.results || []).map((doc) => ({
          value: doc.id,
          label: `${doc.nome} (${doc.registro || ""}-${doc.registro_uf || ""})`,
        }))
      );
      setAssessorOptions(
        (assessorsRes.data.results || []).map((ass) => ({
          value: ass.id,
          label: ass.nome,
        }))
      );
    } catch (err) {
      console.error("Erro ao buscar opções de filtro:", err);
      setError("Erro ao carregar opções de filtro.");
      setStateOptions([]);
      setProcedureOptions([]);
      setDoctorOptions([]);
      setAssessorOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  }, [programaId]);

  const loadPatientOptions = async (inputValue) => {
    const currentUserData = getCurrentUser();
    const currentUserType = currentUserData?.tipo_usuario;
    if (!programaId || inputValue.length < 3 || !currentUserType) return [];
    try {
      const response = await api.get(`/pessoas/beneficiarios/`, {
        params: {
          nome: inputValue,
          programa_id: programaId,
          tipo_usuario: currentUserType,
          limit: 50,
        },
      });
      return (response.data.results || []).map((p) => ({
        value: p.id,
        label: `${p.nome} (${p.cpf || "Sem CPF"})`,
      }));
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const barChartData = useMemo(() => {
    if (!rawData || rawData.length === 0 || !rawData[0]?.data) return [];
    return rawData[0].data.map((item) => ({
      month: formatMonth(item.x),
      Procedimentos: item.y ?? 0,
    }));
  }, [rawData]);

  const totalProcedimentos = barChartData.reduce(
    (sum, item) => sum + (item.Procedimentos || 0),
    0
  );
  const mediaMensal =
    barChartData.length > 0
      ? Math.round(totalProcedimentos / barChartData.length)
      : 0;

  return (
    <Card
      className="h-100 border-0 shadow-sm"
      style={{
        borderRadius: "16px",
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Card.Header className="bg-transparent border-0 pb-0">
        <h5 className="fw-semibold mb-0 text-dark">📅 Procedimentos Mensais</h5>
        <small className="text-muted">
          Quantidade total (ano selecionado ou últimos 12 meses)
        </small>
      </Card.Header>

      <Card.Body className="d-flex flex-column mt-3">
        {/* Filtros */}
        <Row className="gx-3 mb-3">
          <Col md={4}>
            <Form.Label className="small fw-semibold">Ano</Form.Label>
            <Select
              options={yearOptions}
              value={selectedYear}
              onChange={setSelectedYear}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small fw-semibold">Estado</Form.Label>
            <Select
              options={stateOptions}
              value={selectedState}
              onChange={setSelectedState}
              isClearable
              placeholder="Todos"
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small fw-semibold">Paciente</Form.Label>
            <AsyncSelect
              cacheOptions
              loadOptions={loadPatientOptions}
              defaultOptions={false}
              value={selectedPatient}
              onChange={setSelectedPatient}
              placeholder="Buscar..."
              isClearable
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small fw-semibold">Procedimento</Form.Label>
            <Select
              options={procedureOptions}
              value={selectedProcedure}
              onChange={setSelectedProcedure}
              isClearable
              placeholder="Todos"
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small fw-semibold">Médico</Form.Label>
            <Select
              options={doctorOptions}
              value={selectedDoctor}
              onChange={setSelectedDoctor}
              isClearable
              placeholder="Todos"
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small fw-semibold">Assessor</Form.Label>
            <Select
              options={assessorOptions}
              value={selectedAssessor}
              onChange={setSelectedAssessor}
              isClearable
              placeholder="Todos"
            />
          </Col>
        </Row>

        {/* Gráfico */}
        <div
          className="flex-grow-1 d-flex justify-content-center align-items-center"
          style={{ minHeight: "320px" }}
        >
          {(loading || loadingOptions) && (
            <Spinner animation="border" variant="primary" />
          )}
          {error && (
            <Alert variant="danger" className="text-center">
              {error}
            </Alert>
          )}
          {!loading && !loadingOptions && !error && barChartData.length > 0 && (
            <ResponsiveBar
              data={barChartData}
              keys={["Procedimentos"]}
              indexBy="month"
              margin={{ top: 20, right: 30, bottom: 50, left: 50 }}
              padding={0.3}
              colors={{ scheme: "set2" }}
              borderColor={{ from: "color", modifiers: [["darker", 1.3]] }}
              axisBottom={{
                legend: "Mês",
                legendPosition: "middle",
                legendOffset: 36,
              }}
              axisLeft={{
                legend: "Quantidade",
                legendPosition: "middle",
                legendOffset: -40,
              }}
              labelTextColor={{ from: "color", modifiers: [["darker", 2]] }}
              enableGridY
              motionConfig="gentle"
              tooltip={({ indexValue, value }) => (
                <div className="p-2 bg-white rounded shadow-sm border">
                  <strong>{indexValue}</strong>: {value}
                </div>
              )}
            />
          )}
          {!loading &&
            !loadingOptions &&
            !error &&
            barChartData.length === 0 && (
              <p className="text-muted">Nenhum dado encontrado.</p>
            )}
        </div>

        {/* Indicadores */}
        {!loading && !loadingOptions && !error && barChartData.length > 0 && (
          <Row className="mt-4 border-top pt-3 text-center">
            <Col>
              <small className="text-muted d-block">Total Procedimentos</small>
              <h5 className="fw-bold mb-0 text-primary">
                {totalProcedimentos}
              </h5>
            </Col>
            <Col>
              <small className="text-muted d-block">Média Mensal</small>
              <h5 className="fw-bold mb-0 text-primary">{mediaMensal}</h5>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

MonthlyProceduresDashboard.propTypes = {
  ...programaProp,
};

export default MonthlyProceduresDashboard;
